
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";

// Auth Provider
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./context/AuthContext";

// Pages
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Pricing from "./pages/Pricing";
import Account from "./pages/Account";
import Onboard from "./pages/Onboard";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import LunaChat from "./pages/LunaChat";
import Inventory from "./pages/Inventory";
import EditInventoryItem from "./pages/EditInventoryItem";
import RecipeSuggestions from "./pages/RecipeSuggestions";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";

const queryClient = new QueryClient();

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div>Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

// OnboardingCheck component
const OnboardingCheck = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const [profileChecked, setProfileChecked] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) return;
      
      try {
        const { data } = await supabase
          .from('profiles')
          .select('persona')
          .eq('id', user.id)
          .single();
        
        // If no persona is set, user needs to complete onboarding
        setHasCompletedOnboarding(Boolean(data?.persona));
        setProfileChecked(true);
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        setProfileChecked(true);
      }
    };
    
    if (user && !loading) {
      checkOnboardingStatus();
    }
  }, [user, loading]);
  
  if (loading || !profileChecked) {
    return <div>Loading...</div>;
  }
  
  if (user && !hasCompletedOnboarding) {
    return <Navigate to="/onboard" replace />;
  }
  
  return <>{children}</>;
};

// Household-only route component
const HouseholdRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const [isHousehold, setIsHousehold] = useState(false);
  const [checkingPersona, setCheckingPersona] = useState(true);
  
  useEffect(() => {
    const checkHouseholdPersona = async () => {
      if (!user) return;
      
      try {
        const { data } = await supabase
          .from('profiles')
          .select('persona')
          .eq('id', user.id)
          .single();
        
        setIsHousehold(data?.persona === 'household');
        setCheckingPersona(false);
      } catch (error) {
        console.error("Error checking household persona:", error);
        setCheckingPersona(false);
      }
    };
    
    if (user && !loading) {
      checkHouseholdPersona();
    }
  }, [user, loading]);
  
  if (loading || checkingPersona) {
    return <div>Loading...</div>;
  }
  
  if (user && !isHousehold) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <AnimatePresence mode="wait">
              <Routes>
                <Route path="/" element={<Index />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <OnboardingCheck>
                        <Dashboard />
                      </OnboardingCheck>
                    </ProtectedRoute>
                  } 
                />
                <Route path="/pricing" element={<Pricing />} />
                <Route 
                  path="/account" 
                  element={
                    <ProtectedRoute>
                      <Account />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/onboard" 
                  element={
                    <ProtectedRoute>
                      <Onboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/luna-chat" 
                  element={
                    <ProtectedRoute>
                      <LunaChat />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/inventory" 
                  element={
                    <ProtectedRoute>
                      <OnboardingCheck>
                        <Inventory />
                      </OnboardingCheck>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/inventory/edit/:itemId" 
                  element={
                    <ProtectedRoute>
                      <OnboardingCheck>
                        <EditInventoryItem />
                      </OnboardingCheck>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/recipes" 
                  element={
                    <ProtectedRoute>
                      <OnboardingCheck>
                        <HouseholdRoute>
                          <RecipeSuggestions />
                        </HouseholdRoute>
                      </OnboardingCheck>
                    </ProtectedRoute>
                  } 
                />
                <Route path="/auth" element={<Auth />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AnimatePresence>
          </main>
          <Footer />
        </div>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
